// ============================================
// ADMIN SIDEBAR — FAZ 1: Yeniden gruplandırma
// ============================================

import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  Building2,
  Anchor,
  Briefcase,
  FileText,
  Award,
  Star,
  ShieldCheck,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Medal,
  X,
} from 'lucide-react';
import { cn } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useTranslation } from '@crew-at-sea/i18n';

interface AdminSidebarProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onMobileClose?: () => void;
}

export function AdminSidebar({ collapsed, onCollapsedChange, onMobileClose }: AdminSidebarProps) {
  const { t } = useTranslation('panel');
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { logout } = useAuthStore();

  const sidebarSections = [
    {
      // Gösterge Paneli — başlıksız
      items: [
        { id: 'dashboard', label: t('sidebar.dashboard'), href: '/', icon: <LayoutDashboard className="h-5 w-5" /> },
      ],
    },
    {
      title: t('sidebar.management'),
      items: [
        { id: 'users', label: t('sidebar.users'), href: '/users', icon: <Users className="h-5 w-5" /> },
        { id: 'companies', label: t('sidebar.companies'), href: '/companies', icon: <Building2 className="h-5 w-5" /> },
        { id: 'seafarers', label: t('sidebar.seafarers'), href: '/seafarers', icon: <Anchor className="h-5 w-5" /> },
        { id: 'jobs', label: t('sidebar.jobs'), href: '/jobs', icon: <Briefcase className="h-5 w-5" /> },
        { id: 'applications', label: t('sidebar.applications'), href: '/applications', icon: <FileText className="h-5 w-5" /> },
      ],
    },
    {
      title: t('sidebar.moderation', { defaultValue: 'Moderasyon' }),
      items: [
        { id: 'verifications', label: t('sidebar.verifications'), href: '/verifications', icon: <ShieldCheck className="h-5 w-5" /> },
        { id: 'reviews', label: t('sidebar.reviews'), href: '/reviews', icon: <Star className="h-5 w-5" /> },
      ],
    },
    {
      title: t('sidebar.systemDefinitions', { defaultValue: 'Sistem Tanımları' }),
      items: [
        { id: 'certificates', label: t('sidebar.certificates'), href: '/certificates', icon: <Award className="h-5 w-5" /> },
        { id: 'ranks', label: t('sidebar.ranks', { defaultValue: 'Rütbeler' }), href: '/ranks', icon: <Medal className="h-5 w-5" /> },
      ],
    },
    {
      title: t('sidebar.settingsGroup', { defaultValue: 'Ayarlar' }),
      items: [
        { id: 'settings', label: t('sidebar.settings'), href: '/settings', icon: <Settings className="h-5 w-5" /> },
      ],
    },
  ];

  const isActive = (href: string) => {
    if (href === '/') return currentPath === '/';
    return currentPath.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <aside
      className={cn(
        'flex flex-col h-full bg-admin-sidebar text-white transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          'flex items-center h-16 px-4 border-b border-white/10',
          collapsed ? 'justify-center' : 'gap-3'
        )}
      >
        <div className="flex items-center justify-center w-10 h-10">
          <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
        </div>
        {!collapsed && (
          <div className="flex-1">
            <h1 className="text-lg font-bold">Crew At Sea</h1>
            <p className="text-xs text-white/60">{t('sidebar.adminPanel')}</p>
          </div>
        )}
        {/* Mobil kapatma butonu */}
        {!collapsed && onMobileClose && (
          <button
            onClick={onMobileClose}
            className="lg:hidden p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-maritime transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 scrollbar-thin">
        {sidebarSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className={cn(sectionIndex > 0 && 'mt-6')}>
            {section.title && !collapsed && (
              <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-white/40">
                {section.title}
              </h3>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <Link
                  key={item.id}
                  to={(item as any).disabled ? '#' : item.href}
                  onClick={(e: React.MouseEvent) => {
                    if ((item as any).disabled) e.preventDefault();
                  }}
                  className={cn(
                    'flex items-center gap-3 rounded-maritime px-3 py-2.5 text-sm font-medium transition-colors',
                    (item as any).disabled
                      ? 'text-white/30 cursor-not-allowed'
                      : isActive(item.href)
                        ? 'bg-admin-sidebar-active text-white'
                        : 'text-white/70 hover:bg-admin-sidebar-hover hover:text-white',
                    collapsed && 'justify-center px-2'
                  )}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && (
                    <span className="flex-1">
                      {item.label}
                      {(item as any).disabled && (
                        <span className="ml-1 text-[10px] text-white/30">(FAZ 2)</span>
                      )}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Logout Button */}
      <div className="border-t border-white/10 p-2">
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-3 rounded-maritime px-3 py-2.5 text-sm font-medium text-white/70 hover:bg-admin-sidebar-hover hover:text-white transition-colors w-full',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? t('sidebar.logout') : undefined}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>{t('sidebar.logout')}</span>}
        </button>
      </div>

      {/* Collapse Button */}
      <div className="border-t border-white/10 p-2">
        <button
          onClick={() => onCollapsedChange(!collapsed)}
          className={cn(
            'flex items-center gap-3 rounded-maritime px-3 py-2 text-sm font-medium text-white/70 hover:bg-admin-sidebar-hover hover:text-white transition-colors w-full',
            collapsed && 'justify-center px-2'
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5" />
              <span>{t('sidebar.collapse')}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
